'use client'

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGuessNumber } from '../hooks/useGuessNumber'
import { useTelegramBotAPI } from '../hooks/useTelegramBotAPI'
import { GuessGlyph } from '../components/lucky/ProfileGlyphs'
import { TrophyGlyph } from '../components/lucky/ProfileGlyphs'
import { useLocale, useT } from '../contexts/LocaleContext'

const MAX_GUESSES = 10
const REVEAL_HOUR = 23
const REVEAL_MINUTE = 30

export default function GuessNumberPage() {
  const navigate = useNavigate()
  const t = useT()
  const { dir } = useLocale()
  const { showAlert } = useTelegramBotAPI()
  const { status, loading, error, submitGuess, fetchWinners, refetch } = useGuessNumber()
  const [guess, setGuess] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [lastResult, setLastResult] = useState<any>(null)
  const [countdown, setCountdown] = useState<string>('')
  const [winners, setWinners] = useState<any[]>([])

  const loadWinners = useCallback(async () => {
    const data = await fetchWinners()
    return data
  }, [fetchWinners])

  useEffect(() => {
    const tick = () => {
      const now = new Date()
      const reveal = new Date()
      reveal.setHours(REVEAL_HOUR, REVEAL_MINUTE, 0, 0)
      if (now > reveal) {
        reveal.setDate(reveal.getDate() + 1)
      }
      const diff = reveal.getTime() - now.getTime()
      const h = Math.floor(diff / 3600000)
      const m = Math.floor((diff % 3600000) / 60000)
      const s = Math.floor((diff % 60000) / 1000)
      setCountdown(
        `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`,
      )
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    if (!status) return
    const now = new Date()
    const isRevealTime = now.getHours() >= REVEAL_HOUR && now.getMinutes() >= REVEAL_MINUTE
    if (isRevealTime && !status.is_revealed) {
      refetch()
      loadWinners().then(setWinners)
    }
  }, [status, refetch, loadWinners])

  useEffect(() => {
    if (status?.is_revealed) {
      loadWinners().then(setWinners)
    }
  }, [status?.is_revealed, loadWinners])

  const handleGuess = async () => {
    if (guess.length !== 7 || !/^\d{7}$/.test(guess)) {
      showAlert(t('guess.errSevenDigits'))
      return
    }
    if (guess.includes('4')) {
      showAlert(t('guess.errNoFour'))
      return
    }

    setSubmitting(true)
    try {
      const result = await submitGuess(guess)
      setGuess('')
      setLastResult(result)
      if (result) {
        showAlert(result.message)
      }
    } catch (err: any) {
      showAlert(err?.message || t('guess.errSubmit'))
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="lucky-guess mini-app-container pb-20 flex justify-center items-center min-h-screen">
        <div className="lucky-guess__empty">{t('common.loading')}</div>
      </div>
    )
  }

  if (!status) {
    return (
      <div className="lucky-guess mini-app-container pb-20 min-h-screen">
        <div className="lucky-guess__inner">
          <div className="lucky-guess__card lucky-guess__empty">{error || t('guess.statusUnavailable')}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="lucky-guess mini-app-container pb-20 min-h-screen">
      <div className="lucky-guess__inner">
        <header className="lucky-guess__header">
          <button type="button" className="lucky-guess__back" onClick={() => navigate(-1)} aria-label={t('common.back')}>
            {dir === 'rtl' ? '→' : '←'}
          </button>
          <div className="lucky-guess__header-icon">
            <GuessGlyph size={32} />
          </div>
          <div>
            <h1>{t('guess.title')}</h1>
            <p>{t('guess.subtitle')}</p>
          </div>
        </header>

        {!status.is_revealed && status.has_active_number && (
          <section className="lucky-guess__card lucky-guess__timer">
            <span>{t('guess.revealAt')}</span>
            <strong>{countdown}</strong>
          </section>
        )}

        {status.has_active_number && !status.is_revealed && (
          <section className="lucky-guess__card">
            <h2 className="lucky-guess__title">{t('guess.todayPrompt')}</h2>
            <p className="lucky-guess__hint">{t('guess.hint')}</p>

            <input
              type="text"
              inputMode="numeric"
              value={guess}
              onChange={(e) => setGuess(e.target.value.replace(/\D/g, '').replace(/4/g, '').slice(0, 7))}
              placeholder={t('guess.placeholder')}
              className="lucky-guess__input"
              maxLength={7}
              autoComplete="off"
              dir="ltr"
            />

            <div className="lucky-guess__attempts">
              <span>{t('guess.attemptsLeft')}</span>
              <strong>
                {status.attempts_remaining} / {MAX_GUESSES}
              </strong>
            </div>

            <button
              type="button"
              className="lucky-guess__cta"
              onClick={handleGuess}
              disabled={submitting || status.attempts_remaining === 0 || !guess}
            >
              {submitting ? t('common.submitting') : t('guess.submit')}
            </button>

            {lastResult && <div className="lucky-guess__result">{lastResult.message}</div>}
          </section>
        )}

        {status.is_revealed && (
          <section className="lucky-guess__card lucky-guess__reveal">
            <h2>{t('guess.revealedTitle')}</h2>
            <p className="lucky-guess__number" dir="ltr">
              {status.current_number}
            </p>

            {status.user_has_won ? (
              <div className="lucky-guess__banner lucky-guess__banner--win">{t('guess.bannerWin')}</div>
            ) : (
              <div className="lucky-guess__banner lucky-guess__banner--lose">{t('guess.bannerLose')}</div>
            )}

            {status.winners_count > 0 ? (
              <p className="lucky-guess__winners-count">
                {t('guess.winnersCount', { n: status.winners_count })}
              </p>
            ) : (
              <p className="lucky-guess__winners-count">{t('guess.noWinners')}</p>
            )}
          </section>
        )}

        {!status.has_active_number && !status.is_revealed && (
          <section className="lucky-guess__card lucky-guess__waiting">
            <p>{t('guess.waitingNoNumber')}</p>
            <span>{t('guess.waitingSchedule')}</span>
            <em>{t('guess.untilReveal', { countdown })}</em>
          </section>
        )}

        {status.is_revealed && winners.length > 0 && (
          <section className="lucky-guess__card">
            <h3 className="lucky-guess__section-title">
              <TrophyGlyph size={20} />
              {t('guess.winnersToday')}
            </h3>
            <div className="lucky-guess__board">
              {winners.map((w, idx) => (
                <div key={w.id} className="lucky-guess__row">
                  <span className="lucky-guess__rank">#{idx + 1}</span>
                  <div className="lucky-guess__row-text">
                    <strong>{t('guess.userHash', { id: w.user_id })}</strong>
                    <span dir="ltr">{w.guess_number}</span>
                  </div>
                  {w.is_winner && <em>{t('guess.correctTag')}</em>}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  )
}
